import type { z } from "zod";
import { env } from "@/env";
import {
	type ProductDetail,
	ProductDetailSchema,
	type ProductPage,
	ProductPageSchema,
	type ProductStatus,
	type StepsData,
	StepsDataSchema,
} from "./schema";

/** Base URL of the core API (`VITE_API_URL`). */
export const API_URL: string = env.VITE_API_URL.replace(/\/$/, "");

/** Page size of the catalogue: the whole catalogue fits in one page for now. */
export const CATALOG_LIMIT = 100;

export class ContentError extends Error {
	constructor(
		message: string,
		public readonly cause?: unknown,
		/** HTTP status when the server answered with an error. */
		public readonly status?: number,
		/** `Retry-After` of a 429 (rate limit), in ms. */
		public readonly retryAfterMs?: number,
	) {
		super(message);
		this.name = "ContentError";
	}
}

/** True for the error of a request refused by a rate limit (429). */
export const isRateLimited = (error: unknown): error is ContentError =>
	error instanceof ContentError && error.status === 429;

function httpError(url: string, res: Response): ContentError {
	// never show a presigned URL (signature) in the UI
	const where = url.split("?")[0];
	const retryAfter = Number(res.headers.get("Retry-After"));
	return new ContentError(
		`${where} → HTTP ${res.status}`,
		undefined,
		res.status,
		res.status === 429 && retryAfter > 0 ? retryAfter * 1000 : undefined,
	);
}

/** A file of the products bucket (presigned URL): no cookies, S3 does not want them. */
async function fetchJson(url: string, signal?: AbortSignal): Promise<unknown> {
	const res = await fetch(url, { signal });
	if (!res.ok) throw httpError(url, res);
	return res.json();
}

/**
 * A call to the core API, with the session cookie (`credentials: "include"`: the API is another
 * origin of the same site). A JSON `body` is sent as such; an empty answer (202, 204) is null.
 */
export async function apiFetch(
	path: string,
	init: RequestInit = {},
): Promise<unknown> {
	const url = `${API_URL}${path}`;
	const headers = new Headers(init.headers);
	if (init.body !== undefined && !headers.has("Content-Type"))
		headers.set("Content-Type", "application/json");
	const res = await fetch(url, { ...init, headers, credentials: "include" });
	if (!res.ok) throw httpError(url, res);
	const text = await res.text();
	return text ? JSON.parse(text) : null;
}

export function parse<T>(schema: z.ZodType<T>, data: unknown, what: string): T {
	const parsed = schema.safeParse(data);
	if (!parsed.success) throw new ContentError(`${what} invalide`, parsed.error);
	return parsed.data;
}

/** Catalogue search (name, designation or article number, accent-insensitive). */
export async function searchProducts(
	q: string,
	signal?: AbortSignal,
	options: { status?: ProductStatus } = {},
): Promise<ProductPage> {
	const params = new URLSearchParams({ limit: String(CATALOG_LIMIT) });
	if (q.trim()) params.set("q", q.trim());
	if (options.status) params.set("status", options.status);
	const data = await apiFetch(`/products?${params}`, { signal });
	return parse(ProductPageSchema, data, "Catalogue");
}

/** A product and, once it is ready, the presigned URLs of its 3D guide. */
export async function loadProduct(
	slug: string,
	signal?: AbortSignal,
): Promise<ProductDetail> {
	const data = await apiFetch(`/products/${encodeURIComponent(slug)}`, {
		signal,
	});
	return parse(ProductDetailSchema, data, `Produit « ${slug} »`);
}

/** `steps.json` of a product, from its presigned URL. */
export async function loadSteps(
	url: string,
	slug: string,
	signal?: AbortSignal,
): Promise<StepsData> {
	return parse(
		StepsDataSchema,
		await fetchJson(url, signal),
		`steps.json de « ${slug} »`,
	);
}
